import _ from "lodash";
import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import { useShallow } from "zustand/react/shallow";
import Breadcrumb from "../../components/Breadcrumb";
import Search from "../../components/form/Search";
import RouteLink from "../../components/misc/RouteLink";
import MobileListActionBar from "../../components/mobile-action-bar/MobileListActionBar";
import Pagination from "../../components/Pagination";
import SortableHeader from "../../components/SortableHeader";
import useResponsiveLimit from "../../composables/useResponsiveLimit";
import { Role, useAuthStore } from "../../stores/auth";
import {
  buildRestoredListQuery,
  LIST_SECTION,
  useListPreferencesStore,
} from "../../stores/list_preferences";
import {
  WORK_ORDER_DUE_FILTER,
  WORK_ORDER_FILTER,
  useWorkOrdersStore,
} from "../../stores/work_orders";
import { useUserStore } from "../../stores/users";
import { formatDate, formatDateTime, formatTime } from "../../utils/dateFormat";
import { countFileTypes } from "../../utils/files";
import { formatRange } from "../../utils/rangeFormat";
import { DUE_STATE, getDueState, WORK_ORDER_VIEW } from "./utils";
import WorkOrdersMap from "./WorkOrdersMap";

const DEFAULT_SORT_BY = "scheduled";
const DEFAULT_SORT_DIR = "asc";
const ZIP_GROUP_WILDCARD = "x";
const ZIP_FILTER_MENU_STYLE = Object.freeze({ maxHeight: "50vh", overflowY: "auto" });
const DUE_ROW_CLASS = Object.freeze({
  [DUE_STATE.OVERDUE]: "table-danger",
  [DUE_STATE.DUE]: "table-warning",
  [DUE_STATE.NONE]: "",
});

const formatAddress = (property) => {
  const address = property?.addresses?.[0];
  if (!address) return "";
  return [address.street, [address.zip, address.city].filter(Boolean).join(" ")].filter(Boolean).join(", ");
};

const formatZipPrefix = (option) => (option.is_group ? `${option.prefix}${ZIP_GROUP_WILDCARD}` : option.prefix);

const formatZipOption = (option) => `${formatZipPrefix(option)} (${option.count})`;

function WorkOrderRow({ workOrder, isBackoffice }) {
  const { t } = useTranslation();
  const untilPrefix = t("common.range_until");
  const scheduled = formatRange({
    from: formatDate(workOrder.scheduled_from),
    until: formatDate(workOrder.scheduled_until),
    untilPrefix,
  });
  const availability = formatRange({
    from: formatTime(workOrder.availability_from),
    until: formatTime(workOrder.availability_until),
    fromPrefix: t("common.range_from"),
    untilPrefix,
  });
  const rowClass = DUE_ROW_CLASS[getDueState(workOrder)];
  const propertyAddress = formatAddress(workOrder.customer_property);
  const routeAddress = workOrder.property_route_address;
  const isExternal = !!workOrder.source && workOrder.source !== "portal";
  const fileCounts = countFileTypes(workOrder.files);

  return (
    <tr className={rowClass}>
      <td>
        <Link to={`${workOrder.id}/details`} className="fw-bold">
          {workOrder.number}
        </Link>
        {isExternal && (
          <span
            className="badge bg-default text-dark ms-1"
            title={t(`work_orders.origin_hint.${workOrder.source}`)}
          >
            <i className="fa fa-clipboard-check fa-fw" />
          </span>
        )}
        {workOrder.comments?.length > 0 && (
          <span className="badge bg-default text-dark ms-1" title={t("menu.comments")}>
            <i className="fa fa-comments me-1" />
            {workOrder.comments.length}
          </span>
        )}
        {fileCounts.images > 0 && (
          <span className="badge bg-default text-dark ms-1" title={t("files.images")}>
            <i className="fa fa-image fa-fw me-1"></i>
            {fileCounts.images}
          </span>
        )}
        {fileCounts.pdfs > 0 && (
          <span className="badge bg-default text-dark ms-1" title={t("files.pdfs")}>
            <i className="fa fa-file-pdf fa-fw me-1"></i>
            {fileCounts.pdfs}
          </span>
        )}
        {workOrder.delivery_notes?.map((deliveryNote) => (
          <Link
            key={deliveryNote.id}
            to={`/delivery_notes/${deliveryNote.id}/details`}
            className="badge badge-link bg-default text-dark ms-1"
            title={`${t("delivery_notes.delivery_note")} ${deliveryNote.number}`}
          >
            <i className="fa fa-file-lines fa-fw" />
          </Link>
        ))}
      </td>
      {isBackoffice && <td className="d-none d-md-table-cell">{formatDateTime(workOrder.created_at)}</td>}
      <td>
        {scheduled}
        {availability && <div className="small text-muted">{availability}</div>}
      </td>
      <td>
        <ul className="list-group list-unstyled">
          {!_.isEmpty(workOrder.customer?.number) && <li>{workOrder.customer.number}</li>}
          {!_.isEmpty(workOrder.customer?.name) && <li>{workOrder.customer.name}</li>}
        </ul>
      </td>
      <td>
        <ul className="list-group list-unstyled">
          {!_.isEmpty(workOrder.customer_property?.name?.trim()) && <li>{workOrder.customer_property.name}</li>}
          <li>
            {propertyAddress}
            {routeAddress && <RouteLink address={routeAddress} />}
          </li>
        </ul>
      </td>
      {isBackoffice && (
        <td>
          {workOrder.assigned_to ? (
            <>
              {workOrder.assigned_to.name}
              {workOrder.assigned_to.number && <small className="text-muted"> ({workOrder.assigned_to.number})</small>}
            </>
          ) : (
            <span className="text-muted">{t("work_orders.unassigned")}</span>
          )}
        </td>
      )}
    </tr>
  );
}

function WorkOrders() {
  const { t } = useTranslation();
  const [urlQueryParams, setQueryParams] = useSearchParams();
  const savedListPreferences = useListPreferencesStore(
    (state) => state.preferences[LIST_SECTION.WORK_ORDERS],
  );
  const rememberListPreferences = useListPreferencesStore(
    (state) => state.remember,
  );
  const hasUrlQuery = Boolean(urlQueryParams.toString());
  const restoredQueryString = buildRestoredListQuery(
    urlQueryParams,
    savedListPreferences,
  );
  const queryParams = restoredQueryString
    ? new URLSearchParams(restoredQueryString)
    : urlQueryParams;
  const hasRole = useAuthStore((state) => state.hasRole);
  const isBackoffice = hasRole(Role.ADMINISTRATOR) || hasRole(Role.MANAGER) || hasRole(Role.OFFICE);

  const { byIds, allIds, total, zipOptions, fetchSome, fetchZipOptions } = useWorkOrdersStore(
    useShallow((state) => ({
      byIds: state.byIds,
      allIds: state.allIds,
      total: state.total,
      zipOptions: state.zipOptions,
      fetchSome: state.fetchSome,
      fetchZipOptions: state.fetchZipOptions,
    })),
  );
  const workOrders = allIds.map((id) => byIds[id]).filter(Boolean);

  const { usersByIds, usersAllIds, fetchUsers } = useUserStore(
    useShallow((state) => ({
      usersByIds: state.byIds,
      usersAllIds: state.allIds,
      fetchUsers: state.fetchSome,
    })),
  );
  const technicians = usersAllIds
    .map((id) => usersByIds[id])
    .filter((user) =>
      user?.roles?.some((role) => role.name === Role.TECHNICIAN),
    )
    .sort((left, right) => left.name.localeCompare(right.name));

  const defaultLimit = useResponsiveLimit(10);
  const limit = parseInt(queryParams.get("limit")) || defaultLimit;
  const page = parseInt(queryParams.get("page")) || 0;
  const offset = page * limit;
  const search = queryParams.get("q") || "";
  const filterParam = queryParams.get("filter");
  const defaultFilter = WORK_ORDER_FILTER.OPEN;
  const allowedFilters = Object.values(WORK_ORDER_FILTER);
  const normalizedFilter = allowedFilters.includes(filterParam) ? filterParam : defaultFilter;
  const backofficeOnlyFilters = [
    WORK_ORDER_FILTER.MINE,
    WORK_ORDER_FILTER.UNASSIGNED,
    WORK_ORDER_FILTER.CANCELED,
    WORK_ORDER_FILTER.ALL,
  ];
  const filter = !isBackoffice && backofficeOnlyFilters.includes(normalizedFilter)
    ? defaultFilter
    : normalizedFilter;
  const dueParam = queryParams.get("due");
  const due = Object.values(WORK_ORDER_DUE_FILTER).includes(dueParam) ? dueParam : "";
  const assignedToIdParam = queryParams.get("assigned_to_id") || "";
  const assignedToId = isBackoffice && /^\d+$/.test(assignedToIdParam)
    ? assignedToIdParam
    : "";
  const queryString = queryParams.toString();
  const zipPrefixes = useMemo(() => new URLSearchParams(queryString).getAll("zip_prefix"), [queryString]);
  const sortBy = queryParams.get("sort_by") || DEFAULT_SORT_BY;
  const sortDir = queryParams.get("sort_dir") || DEFAULT_SORT_DIR;
  const isMapView = queryParams.get("view") === WORK_ORDER_VIEW.MAP;
  // The list is the default view, so only the map is kept in the URL.
  const viewParam = isMapView ? WORK_ORDER_VIEW.MAP : "";

  useEffect(() => {
    if (restoredQueryString) {
      setQueryParams(restoredQueryString, { replace: true });
    }
  }, [restoredQueryString, setQueryParams]);

  useEffect(() => {
    if (!hasUrlQuery) return;

    rememberListPreferences(LIST_SECTION.WORK_ORDERS, {
      filter,
      due,
      assigned_to_id: assignedToId,
      zip_prefix: zipPrefixes,
      sort_by: sortBy,
      sort_dir: sortDir,
      view: viewParam,
    });
  }, [
    assignedToId,
    due,
    filter,
    hasUrlQuery,
    rememberListPreferences,
    sortBy,
    sortDir,
    viewParam,
    zipPrefixes,
  ]);

  useEffect(() => {
    fetchSome({
      q: search,
      filter,
      due,
      assigned_to_id: assignedToId,
      zip_prefix: zipPrefixes,
      limit,
      offset,
      sort_by: sortBy,
      sort_dir: sortDir,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, page, limit, filter, due, assignedToId, zipPrefixes, sortBy, sortDir]);

  useEffect(() => {
    fetchZipOptions({ q: search, filter, due, assigned_to_id: assignedToId });
  }, [assignedToId, due, fetchZipOptions, filter, search]);

  useEffect(() => {
    if (isBackoffice) {
      fetchUsers({ role: Role.TECHNICIAN, limit: 1000, offset: 0 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBackoffice]);

  const paginationParams = new URLSearchParams(queryParams);
  paginationParams.set("filter", filter);
  if (!due) paginationParams.delete("due");
  if (!assignedToId) paginationParams.delete("assigned_to_id");

  const updateParams = (updates) => {
    const nextParams = new URLSearchParams(queryParams);
    nextParams.set("page", "0");
    if (!due) nextParams.delete("due");
    if (!assignedToId) nextParams.delete("assigned_to_id");
    Object.entries(updates).forEach(([key, value]) => {
      nextParams.delete(key);
      const values = Array.isArray(value) ? value : [value];
      values.filter(Boolean).forEach((item) => {
        nextParams.append(key, item);
      });
    });
    setQueryParams(nextParams);
  };

  const handleSearch = (e) => {
    updateParams({ q: e.target.value });
  };

  const setFilter = (nextFilter) => {
    updateParams({ filter: nextFilter });
  };

  const toggleZipPrefix = (prefix) => {
    const nextZipPrefixes = zipPrefixes.includes(prefix)
      ? zipPrefixes.filter((selectedPrefix) => selectedPrefix !== prefix)
      : [...zipPrefixes, prefix].sort();
    updateParams({ zip_prefix: nextZipPrefixes });
  };

  const clearZipPrefixes = () => {
    updateParams({ zip_prefix: [] });
  };

  const setView = (nextView) => {
    updateParams({ view: nextView === WORK_ORDER_VIEW.MAP ? nextView : "" });
  };

  const handleSort = (column) => {
    const newDir = sortBy === column && sortDir === "desc" ? "asc" : "desc";
    updateParams({ sort_by: column, sort_dir: newDir });
  };

  const handleResetFilters = () => {
    const defaultPreferences = {
      filter: defaultFilter,
      due: "",
      assigned_to_id: "",
      zip_prefix: [],
      sort_by: DEFAULT_SORT_BY,
      sort_dir: DEFAULT_SORT_DIR,
      view: viewParam,
    };
    rememberListPreferences(LIST_SECTION.WORK_ORDERS, defaultPreferences);
    setQueryParams(
      buildRestoredListQuery(new URLSearchParams(), defaultPreferences),
      { replace: true },
    );
  };

  const tabs = isBackoffice
    ? [
        { filter: WORK_ORDER_FILTER.OPEN, label: "work_orders.tabs.open" },
        { filter: WORK_ORDER_FILTER.UNASSIGNED, label: "work_orders.tabs.unassigned" },
        { filter: WORK_ORDER_FILTER.IN_PROGRESS, label: "work_orders.tabs.in_progress" },
        { filter: WORK_ORDER_FILTER.DONE, label: "work_orders.tabs.done" },
        { filter: WORK_ORDER_FILTER.CANCELED, label: "work_orders.tabs.canceled" },
        { filter: WORK_ORDER_FILTER.ALL, label: "work_orders.tabs.all" },
      ]
    : [
        { filter: WORK_ORDER_FILTER.OPEN, label: "work_orders.tabs.open" },
        { filter: WORK_ORDER_FILTER.IN_PROGRESS, label: "work_orders.tabs.in_progress" },
        { filter: WORK_ORDER_FILTER.DONE, label: "work_orders.tabs.done" },
      ];

  const dueFilterColumnClass = isBackoffice ? "col-6 col-lg-3" : "col col-lg-3";
  const zipFilterColumnClass = isBackoffice ? "col-6 col-lg-2" : "col col-lg-2";
  const zipOptionsByPrefix = Object.fromEntries(zipOptions.map((option) => [option.prefix, option]));
  const hasZipSelection = zipPrefixes.length > 0;
  const zipFilterChoices = [
    ...zipPrefixes
      .filter((prefix) => !zipOptionsByPrefix[prefix])
      .map((prefix) => ({ prefix, label: prefix, isSelected: true })),
    ...zipOptions.map((option) => ({
      prefix: option.prefix,
      label: formatZipOption(option),
      isSelected: zipPrefixes.includes(option.prefix),
    })),
  ];
  const zipFilterSummary = hasZipSelection
    ? zipPrefixes
        .map((prefix) => (zipOptionsByPrefix[prefix] ? formatZipPrefix(zipOptionsByPrefix[prefix]) : prefix))
        .join(", ")
    : t("menu.all");

  return (
    <div>
      <Breadcrumb items={[{ label: t("work_orders.work_orders") }]}>
        {isBackoffice && (
          <Link to="add" className="btn btn-red">
            <i className="fa fa-plus fa-fw me-1" />
            {t("work_orders.work_order")}
          </Link>
        )}
      </Breadcrumb>

      <MobileListActionBar>
        {isBackoffice && (
          <Link to="/work_orders/add" className="btn btn-red flex-fill">
            <i className="fa fa-plus fa-fw me-1" />
            {t("work_orders.work_order")}
          </Link>
        )}
      </MobileListActionBar>

      <div className="card border-0">
        <div className="d-md-none px-3 pt-3">
          <select className="form-select" value={filter} onChange={(e) => setFilter(e.target.value)}>
            {tabs.map((tab) => (
              <option key={tab.filter} value={tab.filter}>{t(tab.label)}</option>
            ))}
          </select>
        </div>
        <ul className="nav nav-tabs nav-tabs-v2 px-3 d-none d-md-flex">
          {tabs.map((tab) => (
            <li key={tab.filter} className="nav-item me-2">
              <button onClick={() => setFilter(tab.filter)} className={`nav-link px-2 ${filter === tab.filter && "active"}`}>
                {t(tab.label)}
              </button>
            </li>
          ))}
        </ul>
        <div className="tab-content p-3">
          <div className="tab-pane fade show active">
            <div className="row g-2 mb-3">
              <div className="col-12 col-lg">
                <label className="form-label" htmlFor="work-order-search">
                  {t("menu.search")}
                </label>
                <Search
                  inputId="work-order-search"
                  className="input-group"
                  handleSearch={handleSearch}
                  search={search}
                />
              </div>
              <div className={dueFilterColumnClass}>
                <label className="form-label" htmlFor="work-order-due-filter">
                  {t("work_orders.due_filter")}
                </label>
                <select
                  id="work-order-due-filter"
                  className="form-select"
                  value={due}
                  onChange={(e) => updateParams({ due: e.target.value })}
                >
                  <option value="">{t("menu.all")}</option>
                  <option value={WORK_ORDER_DUE_FILTER.DUE}>{t("work_orders.due")}</option>
                  <option value={WORK_ORDER_DUE_FILTER.OVERDUE}>{t("work_orders.overdue")}</option>
                  <option value={WORK_ORDER_DUE_FILTER.NOT_DUE}>{t("work_orders.not_due")}</option>
                </select>
              </div>
              <div className={zipFilterColumnClass}>
                <label className="form-label" htmlFor="work-order-zip-filter">
                  {t("work_orders.zip_filter")}
                </label>
                <div className="dropdown">
                  <button
                    id="work-order-zip-filter"
                    type="button"
                    className="form-select text-start text-truncate"
                    data-bs-toggle="dropdown"
                    data-bs-auto-close="outside"
                    aria-expanded="false"
                    title={zipFilterSummary}
                    disabled={zipFilterChoices.length === 0}
                  >
                    {zipFilterSummary}
                  </button>
                  <ul className="dropdown-menu w-100" style={ZIP_FILTER_MENU_STYLE} aria-labelledby="work-order-zip-filter">
                    {zipFilterChoices.map((choice) => (
                      <li key={choice.prefix}>
                        <label className="dropdown-item d-flex align-items-center gap-2 mb-0">
                          <input
                            type="checkbox"
                            className="form-check-input mt-0"
                            checked={choice.isSelected}
                            onChange={() => toggleZipPrefix(choice.prefix)}
                          />
                          {choice.label}
                        </label>
                      </li>
                    ))}
                    {hasZipSelection && (
                      <>
                        <li>
                          <hr className="dropdown-divider" />
                        </li>
                        <li>
                          <button type="button" className="dropdown-item" onClick={clearZipPrefixes}>
                            {t("work_orders.zip_filter_clear")}
                          </button>
                        </li>
                      </>
                    )}
                  </ul>
                </div>
              </div>
              {isBackoffice && (
                <div className="col col-lg-3">
                  <label className="form-label" htmlFor="work-order-assignee-filter">
                    {t("work_orders.assigned_to")}
                  </label>
                  <select
                    id="work-order-assignee-filter"
                    className="form-select"
                    value={assignedToId}
                    onChange={(e) => updateParams({ assigned_to_id: e.target.value })}
                  >
                    <option value="">{t("menu.all")}</option>
                    {technicians.map((technician) => (
                      <option key={technician.id} value={technician.id}>
                        {technician.name}
                        {technician.number ? ` (${technician.number})` : ""}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="col-auto ms-auto d-flex align-items-end gap-2">
                <button
                  type="button"
                  className="btn btn-default"
                  onClick={handleResetFilters}
                  aria-label={t("common.reset_filters")}
                  title={t("common.reset_filters")}
                >
                  <i className="fa fa-rotate-left fa-fw" />
                </button>
                <div className="btn-group" role="group" aria-label={t("work_orders.view")}>
                  <button
                    type="button"
                    className={`btn btn-default${isMapView ? "" : " active"}`}
                    onClick={() => setView(WORK_ORDER_VIEW.LIST)}
                    aria-pressed={!isMapView}
                    aria-label={t("work_orders.view_list")}
                    title={t("work_orders.view_list")}
                  >
                    <i className="fa fa-list fa-fw" />
                  </button>
                  <button
                    type="button"
                    className={`btn btn-default${isMapView ? " active" : ""}`}
                    onClick={() => setView(WORK_ORDER_VIEW.MAP)}
                    aria-pressed={isMapView}
                    aria-label={t("work_orders.view_map")}
                    title={t("work_orders.view_map")}
                  >
                    <i className="fa fa-map fa-fw" />
                  </button>
                </div>
              </div>
            </div>
            {isMapView ? (
              <WorkOrdersMap
                q={search}
                filter={filter}
                due={due}
                assignedToId={assignedToId}
                zipPrefixes={zipPrefixes}
                isBackoffice={isBackoffice}
              />
            ) : (
              <>
                <div className="table-responsive mb-3">
                  <table className="table table-hover table-panel text-nowrap align-middle mb-0">
                    <thead>
                      <tr>
                        <SortableHeader column="number" label={t("work_orders.number")} sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                        {isBackoffice && (
                          <SortableHeader column="created_at" label={t("work_orders.created_at")} sortBy={sortBy} sortDir={sortDir} onSort={handleSort} className="d-none d-md-table-cell" />
                        )}
                        <SortableHeader column="scheduled" label={t("work_orders.scheduled_short")} sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                        <th>{t("customers.customer")}</th>
                        <SortableHeader column="zip" label={t("customers.properties.property")} sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                        {isBackoffice && (
                          <SortableHeader column="assigned_to" label={t("work_orders.assigned_to")} sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {workOrders.map((workOrder) => (
                        <WorkOrderRow
                          key={workOrder.id}
                          workOrder={workOrder}
                          isBackoffice={isBackoffice}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
                <Pagination
                  basePath="/work_orders"
                  page={page}
                  limit={limit}
                  total={total}
                  queryParams={paginationParams}
                  label={t("work_orders.work_orders")}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default WorkOrders;
